<template>
  <div class="page-content">
    <div class="container has-text-left">
      <div class="section">
        <h1 class="title is-2 has-text-grey">
          {{ $t("checkout.finaliseOrder.thankYouForOrdering") }}
        </h1>
        <p
          class="title is-3 has-text-grey"
          v-html="
            $t('checkout.finaliseOrder.orderConfirmationSentTo', {
              email: orderTrackingInfo
                ? orderShippingAddress.customerEmail.value
                : paymentDetails['e_mail']
                ? paymentDetails['e_mail']
                : 'Sie',
            })
          "
        ></p>
      </div>
    </div>
    <div class="container" v-if="orderTrackingInfo">
      <div class="section">
        <div class="columns">
          <div class="column is-full-tablet">
            <div class="order-sum summary-details">
              <h3 class="title is-4 has-text-grey">
                {{ $t("orderDetails.labels.items") }}
              </h3>
              <div class="main-cart p-0">
                <cart-item-list
                  :cart-items="orderTrackingInfo.items"
                  :is-editable="false"
                  has-minimal-items
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <hr class="has-background-grey" />
      <div class="section">
        <h3 class="title is-4 has-text-grey mb-1">{{$t('orderDetails.subHeader')}}</h3>
        <div class="columns is-gapless">
          <div class="column is-half-tablet summary-details">
            <div class="detail__list">
              <div
                v-for="detailEntry in orderDetails"
                :key="`order-sum-entry__${detailEntry.label}`"
                class="detail__entry order-sum__entry"
              >
                <div class="entry__label">
                  {{ detailEntry.label }}
                </div>
                <div class="entry__value">
                  {{ detailEntry.value }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="section">
        <div class="columns is-gapless">
          <div class="column">
            <hr class="has-background-grey" />
            <h3 class="detail__header title is-4 is-uppercase has-text-grey">
              {{$t('checkout.paymentDetails.shippingAddress')}}
            </h3>
            <div class="summary-details">
              <div class="detail__list">
                <div
                  v-for="detailEntry in orderShippingAddress"
                  :key="`order-summary__${detailEntry.label}`"
                  class="detail__entry order-sum__entry"
                >
                  <div class="entry__label">
                    {{ detailEntry.label }}
                  </div>
                  <div class="entry__value">
                    {{ detailEntry.value }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="column">
            <hr class="has-background-grey" />
            <h3 class="detail__header title is-4 is-uppercase has-text-grey">
              {{$t('checkout.paymentDetails.billingAddress')}}
            </h3>
            <div class="summary-details">
              <div class="detail__list">
                <div
                  v-for="detailEntry in orderBillingAddress"
                  :key="`order-billingAddress__${detailEntry.label}`"
                  class="detail__entry order-sum__entry"
                >
                  <div class="entry__label">
                    {{ detailEntry.label }}
                  </div>
                  <div class="entry__value">
                    {{ detailEntry.value }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <hr class="has-background-grey" />
      <div class="section">
        <div class="columns">
          <div class="column is-half-tablet">
            <div class="order-sum summary-details">
              <h3 class="title is-4 has-text-grey">{{$t('orderDetails.labels.summaryCaps')}}</h3>
              <div class="detail__list">
                <div class="detail__entry">
                  <span class="order-sum__label">{{
                    orderPaymentDetails.paymentMethod.label
                  }}</span
                  ><span class="order-sum__value">{{
                    orderPaymentDetails.paymentMethod.value
                  }}</span>
                </div>
                <div class="detail__entry">
                  <span class="order-sum__label">{{
                    orderDetails.productionTime.label
                  }}</span
                  ><span class="order-sum__value">{{
                    $tc(
                      "checkout.productionTimeDays",
                      getMaagtechnicProductionTime
                    )
                  }}</span>
                </div>
                <div class="detail__entry">
                  <span class="order-sum__label">{{$t('orderDetails.labels.deliveryTimeShort')}}</span
                  ><span class="order-sum__value">{{
                    $tc(
                      "checkout.productionTimeDays",
                      orderTrackingInfo.shippingMethod.shippingTimeDays
                    )
                  }}</span>
                </div>
                <div class="detail__entry">
                  <span class="order-sum__label"
                    >{{$t('orderDetails.labels.deliveryAndProductionTime')}}</span
                  ><strong class="order-sum__value">
                    {{
                      $tc(
                        "checkout.productionTimeDays",
                        orderTrackingInfo.shippingMethod.shippingTimeDays +
                        getMaagtechnicProductionTime
                      )
                    }}</strong
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="section">
        <div class="order-sum summary-details">
          <div class="detail__list">
            <div class="detail__entry order-sum__order-sum">
              <div class="order-sum__label">
                {{ orderPaymentDetails.orderSum.label }}
              </div>
              <div class="order-sum__value">
                {{ orderPaymentDetails.orderSum.value | toCents | currency }}
              </div>
            </div>
            <div
              class="detail__entry order-sum__order-sum"
              v-if="orderTrackingInfo.smallOrderSurcharge.netPrice > 0"
            >
              <div class="order-sum__label">
                {{$t('checkout.paymentDetails.minimumQuantitySurcharge')}}
              </div>
              <div class="order-sum__value">
                {{
                  orderTrackingInfo.smallOrderSurcharge.netPrice
                    | toCents
                    | currency
                }}
              </div>
            </div>
            <div class="detail__entry order-sum__order-sum">
              <div class="order-sum__label">
                {{ orderPaymentDetails.shippingPrice.label }}
              </div>
              <div class="order-sum__value">
                {{
                  orderPaymentDetails.shippingPrice.value | toCents | currency
                }}
              </div>
            </div>
            <div
              class="
                detail__entry
                order-sum__entry--primary
                has-background-primary-dark has-text-light
                section
                mt-2
              "
            >
              <div class="order-sum__label">
                <span class="title is-4 has-text-white">{{
                  orderPaymentDetails.sum.label
                }}</span>
                <span>{{ $t("checkout.cart.includingVAT") }}</span>
              </div>
              <strong class="order-sum__value title is-4 has-text-white">
                {{ orderPaymentDetails.sum.value | toCents | currency }}
              </strong>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="container">
      <div class="section">
        <a @click="goToStart()" class="is-size-3">{{$t('account.editEmailAddressForm.backToStart')}}</a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import CartItemList from "./CartItemList";
import { orderProcess } from "../../store/constants/order-process";
import AddressSummary from "./ReviewOrder/AdressSummary";

export default {
  name: "OrderComplete",
  components: { AddressSummary, CartItemList },
  computed: {
    ...mapGetters(["orderTrackingInfo", "paymentDetails", "taxRate", "cumulatedProductionTime"]),
    orderDetails() {
      return {
        customerReference: {
          label: this.$t("checkout.paymentDetails.inputLabels.referenceNumber"),
          value: this.orderTrackingInfo?.customerRemarks?.referenceNumber || "",
        },
        orderID: {
          label: this.$i18n.t("orderDetails.labels.orderNumber"),
          value: this.orderTrackingInfo.orderNumber,
        },
        orderDate: {
          label: this.$i18n.t("orderDetails.labels.createdAt"),
          value: this.$i18n.d(new Date(this.orderTrackingInfo.createdAt)),
        },
        orderState: {
          label: this.$i18n.t("orderDetails.labels.orderStatus"),
          value: this.$i18n.t(
            `checkout.orderStatusLabels.${this.orderTrackingInfo.orderStatus}`
          ),
        },
        productionTime: {
          label: this.$i18n.t("orderDetails.labels.productionTime"),
          value: this.$i18n.tc(
            "checkout.productionTimeDays",
            this.getMaagtechnicProductionTime
          ),
        },
        shippingTime: {
          label: "+" + this.$i18n.t("orderDetails.labels.deliveryTimeShort"),
          value: this.$i18n.tc(
            "checkout.productionTimeDays",
            this.orderTrackingInfo.shippingMethod.shippingTimeDays
          ),
        },
      };
    },
    getMaagtechnicProductionTime() {
      const estimatedProductionTimesByWeekday = {
          0: 9, // Sunday
          1: 8, // Monday
          2: 7, //...
          3: 6,
          4: 5,
          5: 4,
          6: 9, //Saturday
      };

      return estimatedProductionTimesByWeekday[new Date().getDay()];
    },
    orderShippingAddress() {
      return {
        customerName: {
          label: this.$i18n.t("orderDetails.labels.name"),
          value: `${this.orderTrackingInfo.shippingInformation.firstname} ${this.orderTrackingInfo.shippingInformation.lastname}`,
        },
        customerEmail: {
          label: this.$i18n.t("orderDetails.labels.email"),
          value: this.orderTrackingInfo.email,
        },
        customerStreet: {
          label: this.$i18n.t("orderDetails.labels.address"),
          value: `${this.orderTrackingInfo.shippingInformation.street}`,
        },
        customerZipAndCity: {
          label: this.$i18n.t("orderDetails.labels.city"),
          value: `${this.orderTrackingInfo.shippingInformation.postalCode} ${this.orderTrackingInfo.shippingInformation.city}`,
        },
        customerCountry: {
          label: this.$i18n.t("orderDetails.labels.country"),
          value: `${this.orderTrackingInfo.shippingInformation.country.name}`,
        },
      };
    },
    orderBillingAddress() {
      return {
        customerName: {
          label: this.$i18n.t("orderDetails.labels.name"),
          value: `${this.orderTrackingInfo.billingInformation.firstname} ${this.orderTrackingInfo.billingInformation.lastname}`,
        },
        customerEmail: {
          label: this.$i18n.t("orderDetails.labels.email"),
          value: this.orderTrackingInfo.email,
        },
        customerStreet: {
          label: this.$i18n.t("orderDetails.labels.address"),
          value: `${this.orderTrackingInfo.billingInformation.street}`,
        },
        customerZipAndCity: {
          label: this.$i18n.t("orderDetails.labels.city"),
          value: `${this.orderTrackingInfo.billingInformation.postalCode} ${this.orderTrackingInfo.billingInformation.city}`,
        },
        customerCountry: {
          label: this.$i18n.t("orderDetails.labels.country"),
          value: `${this.orderTrackingInfo.billingInformation.country.name}`,
        },
      };
    },
    orderPaymentDetails() {
      return {
        orderSum: {
          label: this.$i18n.t("orderDetails.labels.positionPrice"),
          value: `${this.orderTrackingInfo.positionPrice.toFixed(2)}`,
        },
        shippingPrice: {
          label: this.$i18n.t("orderDetails.labels.shippingPrice"),
          value: `${this.orderTrackingInfo.shippingTotal.toFixed(2)}`,
        },
        sum: {
          label: this.$i18n.t("orderDetails.labels.amountTotal"),
          value: `${parseFloat(
            this.orderTrackingInfo.amountTotal
          ).toFixed(2)}`,
        },
        paymentMethod: {
          label: this.$i18n.t("orderDetails.labels.payment"),
          value: this.$i18n.t(
            `checkout.paymentMethodLabels.${this.orderTrackingInfo.billingInformation.paymentMethod}`
          ),
        },
      };
    },
  },
  methods: {
    goToStart() {
      this.$router.push("/");
    },
    fetchCMSData() {
      this.$store.dispatch("fetchCMSPage", { urlSlug: "/" });
    },
  },
  created() {
    let cookieConsent = localStorage.getItem("cookies");
    let mrSessionId = localStorage.getItem("mrSessionId");
    let userPreferences = localStorage.getItem("userPreferences");
    localStorage.clear();
    localStorage.setItem("cookies", cookieConsent);
    localStorage.setItem("mrSessionId", mrSessionId);
    localStorage.setItem("userPreferences", userPreferences);
    this.fetchCMSData();
    this.$store.dispatch(
      "setCurrentOrderProcessStep",
      orderProcess.STEPS.ORDER_COMPLETED
    );
    this.$store.dispatch("clearCart");
    this.$store.dispatch("discardDiscount");
    this.$store.dispatch("createCart");

    this.$store.dispatch("fetchOrderTrackingInfo", {
      orderId: this.$route.params.orderId,
    });

    if (this.$route.query.token && this.$route.query.PayerID) {
      this.$store.dispatch("handlePayPalCallback", {
        ...this.$route.query,
        success: true,
      });
    }
  },
};
</script>

<style scoped></style>
